import { useState, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle, Lock } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useAuth } from '../contexts/AuthContext';
import { createNewPasswordSchema, type NewPasswordFormData } from '../schemas/authSchemas';
import { supabase } from '@/lib/supabase';

export function ResetPasswordConfirmPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updatePassword } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null);
  const { t } = useTranslation('auth');
  const newPasswordSchema = useMemo(() => createNewPasswordSchema(t), [t]);
  const verified = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  // Establish a recovery session from the URL, then check it is present.
  // Supports three link formats from Supabase email templates:
  //   1. ?token_hash=...&type=recovery     → verifyOtp (new templates)
  //   2. ?code=...                         → exchangeCodeForSession (PKCE)
  //   3. #access_token=...&refresh_token=. → setSession (implicit fallback)
  useEffect(() => {
    if (verified.current) return;
    verified.current = true;

    const tokenHash = searchParams.get('token_hash');
    const type = searchParams.get('type');
    const code = searchParams.get('code');
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');

    const ensureSession = async () => {
      try {
        if (tokenHash && type === 'recovery') {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          });
          if (error) {
            setIsValidSession(false);
            return;
          }
        } else if (code) {
          const existing = await supabase.auth.getSession();
          if (!existing.data.session) {
            const { error } = await supabase.auth.exchangeCodeForSession(code);
            if (error) {
              setIsValidSession(false);
              return;
            }
          }
        } else if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            setIsValidSession(false);
            return;
          }
          window.history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search
          );
        }

        const { data: { session } } = await supabase.auth.getSession();
        setIsValidSession(!!session);
      } catch {
        setIsValidSession(false);
      }
    };

    ensureSession();
  }, [searchParams]);

  const onSubmit = async (data: NewPasswordFormData) => {
    setError(null);
    setIsSubmitting(true);

    try {
      const { error } = await updatePassword(data.password);

      if (error) {
        setError(error.message);
        return;
      }

      setIsSuccess(true);
    } catch {
      setError(t('common.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state while checking session
  if (isValidSession === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </div>
    );
  }

  // Invalid or expired link
  if (!isValidSession) {
    return (
      <div className="min-h-screen flex flex-col justify-center px-4 py-12">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="glass-effect py-8 px-6 rounded-[15px] border border-white/25">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="h-8 w-8 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {t('resetPasswordConfirm.linkExpiredTitle')}
              </h2>
              <p className="text-white/70 mb-6">
                {t('resetPasswordConfirm.linkExpiredDescription')}
              </p>
              <Link
                to="/reset-password"
                className="block w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold
                           hover:bg-pp-orange/90 transition-colors text-center"
              >
                {t('resetPasswordConfirm.requestNewLink')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success message
  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col justify-center px-4 py-12">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="glass-effect py-8 px-6 rounded-[15px] border border-white/25">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {t('resetPasswordConfirm.passwordChangedTitle')}
              </h2>
              <p className="text-white/70 mb-6">
                {t('resetPasswordConfirm.passwordChangedDescription')}
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold
                           hover:bg-pp-orange/90 transition-colors"
              >
                {t('resetPasswordConfirm.goToLogin')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center px-4 py-12">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="https://parentpath.de/pp_logo-piktogram-w100.svg"
            alt="ParentPath"
            className="w-16 h-16"
          />
        </div>

        <h1 className="text-center text-2xl font-bold text-white">
          {t('resetPasswordConfirm.title')}
        </h1>
        <p className="mt-2 text-center text-sm text-white/70">
          {t('resetPasswordConfirm.description')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="glass-effect py-8 px-6 rounded-[15px] border border-white/25">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-[10px] flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Password Input */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                {t('resetPasswordConfirm.newPasswordLabel')}
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('register.passwordPlaceholder')}
                  autoComplete="new-password"
                  className="w-full px-4 py-3 pr-12 rounded-[10px] bg-white/10 border border-white/20
                             text-white placeholder-white/40 caret-white
                             focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                             outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-sm text-red-400">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password Input */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                {t('register.confirmPassword')}
              </label>
              <div className="relative">
                <input
                  {...register('confirmPassword')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder={t('register.confirmPasswordPlaceholder')}
                  autoComplete="new-password"
                  className="w-full px-4 py-3 pr-12 rounded-[10px] bg-white/10 border border-white/20
                             text-white placeholder-white/40 caret-white
                             focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                             outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1.5 text-sm text-red-400">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Password Requirements */}
            <div className="p-4 bg-white/5 rounded-[10px] border border-white/10">
              <p className="text-sm font-medium text-white/70 mb-2 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                {t('register.passwordRequirements')}
              </p>
              <ul className="text-xs text-white/50 space-y-1 pl-6">
                <li>• {t('register.req8Chars')}</li>
                <li>• {t('register.reqUppercase')}</li>
                <li>• {t('register.reqLowercase')}</li>
                <li>• {t('register.reqNumber')}</li>
              </ul>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold
                         hover:bg-pp-orange/90 transition-colors disabled:opacity-50
                         flex items-center justify-center"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('resetPasswordConfirm.changePassword')
              )}
            </button>
          </form>

          {/* Back to Login Link */}
          <p className="mt-6 text-center text-sm text-white/70">
            <Link
              to="/login"
              className="font-medium text-pp-orange hover:text-pp-orange/80"
            >
              {t('resetPasswordConfirm.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
